// @symbo-ls/workspace-files — grid placement, occupancy, and selection-ring
// helpers for the /files desktop.
//
// This module is the SHARED closure behind BOTH the eager node-mutation ops
// (nodeOps.js — createCanvasFolder/moveNodesIntoFolder need placement) AND the
// deferred /files functions (index.js — upload/move/cleanup/stage gestures).
// It lives on its own so `_pendingCells` (the in-flight-placement reservation
// set) is a SINGLE instance across both: a duplicated copy would let a
// folder-create in the eager slice race an upload in the deferred slice onto the
// same cell — exactly the concurrency bug `_pendingCells` exists to prevent.
//
// Pure module state + geometry only (no DOMQL, no `this`); every function takes
// its `root` / DOM node explicitly.

// ── grid placement ─────────────────────────────────────────────────────────
// Lattice sized for the D1 preview tiles (2026-06-10 per Nika "increase file
// thumbs + gap between files"): roomier cells so tiles breathe. Stored
// positions keep their coords and re-snap to this lattice on next move.
const GRID_X0 = 48
const GRID_Y0 = 48
const GRID_DX = 168
const GRID_DY = 188
const GRID_COLS = 7

export const gridPlace = (i) => ({
  x: GRID_X0 + (i % GRID_COLS) * GRID_DX,
  y: GRID_Y0 + Math.floor(i / GRID_COLS) * GRID_DY
})

// Snap an absolute board position to the desktop grid (same lattice as
// gridPlace). Clamps to the FIRST CELL (GRID_X0/Y0), not 0 — a drop left of /
// above the desktop origin used to clamp to 0, which is OFF-lattice
// ((0-48)%168 ≠ 0) and threw off every collision/cleanup calculation around
// that node (per Nika "moving out FilesDesktop messes up grid").
export const snapToGrid = (x, y) => ({
  x: Math.max(GRID_X0, GRID_X0 + Math.round(((x || 0) - GRID_X0) / GRID_DX) * GRID_DX),
  y: Math.max(GRID_Y0, GRID_Y0 + Math.round(((y || 0) - GRID_Y0) / GRID_DY) * GRID_DY)
})

export const _cellKey = (x, y) => x + ',' + y

// Cells claimed by in-flight create/upload calls that haven't yet landed in
// root.fileNodes. Folded into _occupiedCells so concurrent placements — e.g.
// double-clicking "New folder", or an upload racing a folder create — never
// resolve the SAME "first free cell" against a stale node list and stack on
// top of each other (which read as a new folder colliding with another node's
// name). Each claimer adds its cell synchronously via _claimCell and removes
// it via _releaseCell once its row is in fileNodes (or the create failed).
export const _pendingCells = new Set()

// ── position-less nodes ─────────────────────────────────────────────────────
// A node with NO stored position — a null / missing x or y: a row the server
// created without one (a Workspace preset's seeded folders), never a row this
// app writes (every write here is a lattice cell). The desktop places it, so
// the server never has to mirror this lattice.

// Does the row carry a stored position (both axes finite numbers)?
export const _hasStoredSpot = (n) => !!n && Number.isFinite(n.x) && Number.isFinite(n.y)

// THE placement of the position-less nodes: each WAITING one takes the next
// free lattice cell of ITS folder, in reading order, around every node that
// sits somewhere already — a live override (`_fileNodePos`: a drag, a save in
// flight) or a stored position — and every cell an in-flight create holds
// (_pendingCells). The order is stable — creation time, then id — so every
// member's desktop computes the same cells until the first save writes them.
// A position-less node that has an override no longer waits: its save is out
// (saveFileNodeSpot → moveCanvasNode), and it keeps that cell while the others
// land. A node WITH a stored position never moves.
// Pure: `{ [id]: { x, y } }` for the waiting nodes only.
//
// No override is read unless a row has no position. The desktop's child list
// calls this, and each override key it reads ties that list to the key — the
// common desktop (every row positioned) keeps setFileNodePos's narrow write.
export const _unpositionedSpots = (root) => {
  const out = {}
  const nodes = (root && root.fileNodes) || []
  const unplaced = nodes.filter((n) => n && n.id && !_hasStoredSpot(n))
  if (!unplaced.length) return out
  const pos = root._fileNodePos || {}
  const waiting = unplaced.filter((n) => !pos[n.id])
  if (!waiting.length) return out
  const folderOf = (n) => n.parentId || ''
  const folders = new Set(waiting.map(folderOf))
  const taken = {}
  const takenIn = (folder) => taken[folder] || (taken[folder] = new Set(_pendingCells))
  nodes.forEach((n) => {
    if (!n || !folders.has(folderOf(n))) return
    const p = (n.id && pos[n.id]) || (_hasStoredSpot(n) ? n : null)
    if (!p) return
    const c = snapToGrid(p.x, p.y)
    takenIn(folderOf(n)).add(_cellKey(c.x, c.y))
  })
  const stable = (a, b) =>
    String(a.createdAt || '').localeCompare(String(b.createdAt || '')) ||
    String(a.id).localeCompare(String(b.id))
  waiting
    .slice()
    .sort(stable)
    .forEach((n) => {
      const occ = takenIn(folderOf(n))
      let i = 0
      let p = gridPlace(i)
      while (occ.has(_cellKey(p.x, p.y)) && i < 5000) p = gridPlace(++i)
      occ.add(_cellKey(p.x, p.y))
      out[n.id] = { x: p.x, y: p.y }
    })
  return out
}

// Where a node sits NOW: its live override (a drag, a just-saved move), else
// its stored position, else its placed cell. A caller that reads many nodes
// computes `spots` (_unpositionedSpots) once and passes it.
export const _nodeSpot = (root, n, spots) => {
  const pos = (root && root._fileNodePos) || {}
  if (n && pos[n.id]) return pos[n.id]
  if (_hasStoredSpot(n)) return { x: n.x, y: n.y }
  const placed = (spots || _unpositionedSpots(root))[n && n.id]
  return placed || gridPlace(0)
}

// Live snapped cells of the real nodes in the current folder — a position-less
// node counts at its placed cell, so nothing new lands on it.
export const _realNodeCells = (root, excludeId) => {
  const folder = root.filesOpenFolder || null
  const spots = _unpositionedSpots(root)
  const occ = new Set()
  ;(root.fileNodes || []).forEach((n) => {
    if (n.id === excludeId || (n.parentId || null) !== folder) return
    const p = _nodeSpot(root, n, spots)
    const c = snapToGrid(p.x, p.y)
    occ.add(_cellKey(c.x, c.y))
  })
  return occ
}

// Virtual (position-row-less) /documents docs at the root, each assigned the
// next FREE grid cell (never on top of a real node). Single source of truth —
// the renderer places them here and the occupancy check reserves the same cells.
export const _placedVirtualDocs = (root) => {
  if (root.filesOpenFolder || (root.filesScope || 'shared') === 'private') return []
  // Wait for the real rows before gridding virtuals — placing them against an
  // un-fetched (empty) node list reshuffles them when fileNodes lands (flicker).
  if (!root._fileNodesLoaded) return []
  const placed = new Set(
    (root.fileNodes || []).filter((n) => n.kind === 'doc' && n.docId).map((n) => n.docId)
  )
  const occ = _realNodeCells(root, null)
  const out = []
  let scan = 0
  ;(root.fileDocs || [])
    .filter((d) => d.id && !placed.has(d.id))
    .forEach((d) => {
      let p = gridPlace(scan++)
      while (occ.has(_cellKey(p.x, p.y)) && scan < 2000) p = gridPlace(scan++)
      occ.add(_cellKey(p.x, p.y))
      out.push({ docId: d.id, title: d.title, x: p.x, y: p.y })
    })
  return out
}

// Snapped grid cells already occupied in the current folder (so new/dropped
// nodes never stack on the same spot). `excludeId` skips a node (the one moving).
export const _occupiedCells = (root, excludeId) => {
  const occ = _realNodeCells(root, excludeId)
  _placedVirtualDocs(root).forEach((v) => occ.add(_cellKey(v.x, v.y)))
  _pendingCells.forEach((k) => occ.add(k))
  return occ
}

// Nearest free cell to a snapped cell `c` — spirals outward through the grid
// lattice until it finds a cell not in `occ`.
export const _nearestFreeCell = (occ, c) => {
  if (!occ.has(_cellKey(c.x, c.y))) return c
  const col0 = Math.round((c.x - GRID_X0) / GRID_DX)
  const row0 = Math.round((c.y - GRID_Y0) / GRID_DY)
  for (let r = 1; r < 40; r++) {
    for (let dc = -r; dc <= r; dc++) {
      for (let dr = -r; dr <= r; dr++) {
        if (Math.max(Math.abs(dc), Math.abs(dr)) !== r) continue
        const col = col0 + dc
        const row = row0 + dr
        if (col < 0 || row < 0) continue
        const x = GRID_X0 + col * GRID_DX
        const y = GRID_Y0 + row * GRID_DY
        if (!occ.has(_cellKey(x, y))) return { x, y }
      }
    }
  }
  return null
}

// First free grid cell. With a drop `hint` it uses that cell when free, else
// spirals outward to the nearest free one; with no hint it scans reading order.
// `used` reserves extra cells claimed earlier in a multi-file loop.
export const _freeGridCell = (root, hint, excludeId, used) => {
  const occ = _occupiedCells(root, excludeId)
  if (used) used.forEach((k) => occ.add(k))
  if (hint && typeof hint.x === 'number' && typeof hint.y === 'number') {
    const near = _nearestFreeCell(occ, snapToGrid(hint.x, hint.y))
    if (near) return near
  }
  for (let i = 0; i < 2000; i++) {
    const p = gridPlace(i)
    if (!occ.has(_cellKey(p.x, p.y))) return p
  }
  return gridPlace(0)
}

// Claim the next free cell for a NEW node and reserve it against concurrent
// in-flight creates (_pendingCells is folded into _occupiedCells, so the next
// claimer skips it). The caller MUST _releaseCell once the row is in
// fileNodes — by then _realNodeCells counts it, so there's no placement gap.
export const _claimCell = (root, hint, excludeId) => {
  const cell = _freeGridCell(root, hint, excludeId)
  _pendingCells.add(_cellKey(cell.x, cell.y))
  return cell
}
export const _releaseCell = (cell) => {
  if (cell) _pendingCells.delete(_cellKey(cell.x, cell.y))
}

// ── SDK row-id resolver ─────────────────────────────────────────────────────
// Pull the created row's id out of an SDK create result (tolerates {data},
// bare row, or array shapes).
export const _rowId = (res) => {
  const row = res?.data || res
  if (!row) return null
  return row.id || (Array.isArray(row) ? row[0] && row[0].id : null)
}

// ── selection rings ─────────────────────────────────────────────────────────
// Imperative selection-ring sync (first child of each node) — zero-lag feedback
// during sweeps; the reactive style factory keeps them correct across renders.
export const _ringSyncDom = (deskNode, ids) => {
  if (!deskNode) return
  const set = new Set(ids || [])
  try {
    deskNode.querySelectorAll('[data-node-id]').forEach((n) => {
      if (n.firstElementChild)
        n.firstElementChild.style.display = set.has(n.dataset.nodeId) ? 'block' : 'none'
    })
  } catch (err) {
    console.warn('[Files] ring sync failed:', err)
  }
}

// After a create/upload sets filesSelectedIds, sync the selection rings once the
// new node has rendered: a just-mounted node shows its ring reactively, but
// already-mounted nodes don't re-evaluate on a filesSelectedIds change alone, so
// this imperatively clears prior selections and shows every new id's ring.
export const _syncRingsAfterRender = (self, ids) => {
  setTimeout(() => {
    const fm = self.FilesMain || (self.lookup && self.lookup('FilesMain'))
    const desk =
      fm &&
      fm.Body &&
      fm.Body.Stage &&
      fm.Body.Stage.Board &&
      fm.Body.Stage.Board.FilesDesktop &&
      fm.Body.Stage.Board.FilesDesktop.node
    _ringSyncDom(desk, ids || [])
  }, 60)
}

// ── name disambiguation ─────────────────────────────────────────────────────
// Sibling-scoped "New folder" → "New folder 2" → "New folder 3" numbering
// (the common file-manager convention). Mirrors _pendingCells: an in-flight
// create's chosen name is reserved here so a second rapid create (double-
// click "New folder" before the first row has landed in root.fileNodes)
// can't resolve the SAME free name against a stale node list and produce two
// siblings both titled "New folder".
export const _pendingNames = new Set()

// Separator can't appear in a resolved name/parentId pair ambiguously enough
// to matter here — worst case is an over-cautious extra numbering, never an
// under-cautious collision.
const _nameReserveKey = (parentId, name) =>
  (parentId || 'root') + '::' + String(name).trim().toLowerCase()

// First unused name among real siblings (same parentId) + any name claimed
// by an in-flight create for that same parentId. `baseName` is returned
// as-is when free; otherwise numbered from 2 ("New folder 2", "New folder 3", …).
export const _uniqueSiblingName = (root, baseName, parentId) => {
  const base = String(baseName || '').trim() || 'New folder'
  const taken = new Set(
    (root.fileNodes || [])
      .filter((n) => (n.parentId || null) === (parentId || null))
      .map((n) =>
        String(n.name || '')
          .trim()
          .toLowerCase()
      )
  )
  let candidate = base
  let i = 1
  while (
    taken.has(candidate.toLowerCase()) ||
    _pendingNames.has(_nameReserveKey(parentId, candidate))
  ) {
    i += 1
    candidate = `${base} ${i}`
  }
  return candidate
}

export const _claimName = (parentId, name) => {
  const key = _nameReserveKey(parentId, name)
  _pendingNames.add(key)
  return key
}
export const _releaseName = (key) => {
  if (key) _pendingNames.delete(key)
}

// ── folder tree ─────────────────────────────────────────────────────────────
// Ancestor chain of a folder (for cycle guards — a folder can't be dropped
// into itself or its own subtree).
export const _folderAncestors = (root, folderId) => {
  const byId = {}
  ;(root.fileNodes || []).forEach((n) => {
    byId[n.id] = n
  })
  const chain = new Set()
  let cur = folderId
  let guard = 0
  while (cur && byId[cur] && guard++ < 64) {
    chain.add(cur)
    cur = byId[cur].parentId || null
  }
  return chain
}
